// DeniedOrdersSection.jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  IoAlertCircle,
  IoDocumentText,
  IoCloudUploadOutline,
  IoCheckmarkCircle,
  IoTimeOutline,
  IoArrowBack,
} from "react-icons/io5";
const DeniedOrdersSection = ({ API_BASE_URL, onClose }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const [files, setFiles] = useState({});

  useEffect(() => {
    fetchDeniedOrders();
  }, []);

  const fetchDeniedOrders = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_BASE_URL}/orders/denied/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setOrders(data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleResubmit = async (orderId) => {
    const file = files[orderId];
    if (!file) {
      alert('Please select a document to upload');
      return;
    }

    setUploading(prev => ({ ...prev, [orderId]: true }));
    
    try {
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('document', file);

      const res = await fetch(`${API_BASE_URL}/orders/${orderId}/resubmit-documents/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();

      if (data.success) {
        alert('Documents submitted successfully. Awaiting biller review.');
        fetchDeniedOrders(); // refresh
      } else {
        alert(data.error || 'Failed to submit');
      }
    } catch (e) {
      alert('Failed to submit documents');
    } finally {
      setUploading(prev => ({ ...prev, [orderId]: false }));
    }
  };

  if (loading) return <div>Loading denied orders...</div>;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Denied Orders</h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕ Close</button>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No denied orders.</div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => (
            <div key={order.id} className="border border-red-200 bg-red-50 rounded-2xl p-5">
              
              {/* Order Header */}
              <div className="flex items-center justify-between mb-3">
                <span className="font-bold text-gray-900">#{order.order_number}</span>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  order.can_resubmit 
                    ? 'bg-amber-100 text-amber-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {order.can_resubmit ? 'Resubmission Available' : 'Final Denial — No More Attempts'}
                </span>
              </div>

              {/* Patient & Order Info */}
              <p className="text-sm text-gray-700 mb-1">
                <strong>Patient:</strong> {order.patient_name}
              </p>
              <p className="text-sm text-gray-700 mb-1">
                <strong>Care Type:</strong> {order.care_type}
              </p>
              <p className="text-sm text-gray-700 mb-1">
                <strong>Wound:</strong> {order.wound_type} — {order.wound_location}
              </p>

              {/* Biller Notes */}
              {order.medical_specialist_notes && (
                <div className="mt-3 p-3 bg-white border border-red-200 rounded-xl">
                  <p className="text-xs font-bold text-red-700 uppercase mb-1">
                    Biller Notes (Reason for Denial)
                  </p>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">
                    {order.medical_specialist_notes}
                  </p>
                </div>
              )}

              {/* Resubmit Section */}
              {order.can_resubmit ? (
                <div className="mt-4 p-3 bg-white border border-amber-200 rounded-xl">
                  <p className="text-xs font-bold text-amber-700 uppercase mb-2">
                    Upload Supporting Document
                  </p>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setFiles(prev => ({ 
                      ...prev, [order.id]: e.target.files[0] 
                    }))}
                    className="block w-full text-sm text-gray-600 mb-3"
                  />
                  <button
                    onClick={() => handleResubmit(order.id)}
                    disabled={uploading[order.id] || !files[order.id]}
                    className="w-full py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold text-sm disabled:opacity-50"
                  >
                    {uploading[order.id] ? 'Submitting...' : 'Submit for Re-review'}
                  </button>
                  <p className="text-xs text-red-600 mt-2 font-semibold">
                    ⚠️ This is your only chance to resubmit. Once submitted, no further attempts are allowed.
                  </p>
                </div>
              ) : (
                <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-xl">
                  <p className="text-sm font-bold text-red-700">
                    This order has been denied twice and cannot be resubmitted.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DeniedOrdersSection;